import type { Resource, ResourceType } from '@medplum/fhirtypes'
import { sortBy } from 'lodash-es'
import {
  ExtendedResource,
  type ExtendedResourceConstructor,
} from '#src/ExtendedResource.ts'

export const resourceModelMap: Partial<
  Record<ResourceType, ExtendedResourceConstructor<any>[]>
> = {}

export function registerResourceModel(
  constructorClass: ExtendedResourceConstructor<any>
): void {
  const resourceType = constructorClass.RESOURCE_TYPE as ResourceType

  const existing = resourceModelMap[resourceType] ?? []
  if (!existing.some((ctor) => ctor.name === constructorClass.name)) {
    resourceModelMap[resourceType] = [...existing, constructorClass]
  }
}

/**
 * Calculates the number of hops in the prototype chain from a constructor to ExtendedResource
 * @param constructor - The constructor to calculate depth for
 * @returns The number of prototype chain hops to ExtendedResource
 */
function getPrototypeChainDepth(
  constructor: ExtendedResourceConstructor<any>
): number {
  let depth = 0
  let currentPrototype = constructor.prototype

  while (
    currentPrototype &&
    currentPrototype.constructor !== ExtendedResource
  ) {
    depth++
    currentPrototype = Object.getPrototypeOf(currentPrototype)
  }

  return depth
}

export function getResourceModelConstructor(
  resource: Resource
): ExtendedResourceConstructor<any> | undefined {
  const resourceType = resource.resourceType
  if (resourceType in resourceModelMap) {
    const modelConstructors = resourceModelMap[resourceType]

    // Sort constructors by prototype chain depth (deeper descendants first)
    const sortedConstructors = sortBy(
      modelConstructors,
      (ctor) => -getPrototypeChainDepth(ctor)
    )

    const modelConstructor = sortedConstructors?.find((mc) =>
      mc.isInstance(resource)
    )
    return modelConstructor
  }
  return undefined
}

// Best human-readable name for a resource's most specific Seen model, for
// display (e.g. an icon tooltip). Prefers a coded display the resource already
// carries (`code.text`/`code.display` — how Seen distinguishes "SDR Signature"
// vs "Standard Task", all `Task`), then the matched model class name
// humanized (e.g. `SDRSignatureTask` → "SDR Signature Task"), and finally the
// raw FHIR resourceType.
export function getResourceModelName(resource: Resource): string {
  const coded = codedDisplay(resource)
  if (coded) {
    return coded
  }
  const ctor = getResourceModelConstructor(resource)
  if (ctor) {
    return humanizeModelName(ctor.name)
  }
  return resource.resourceType
}

function codedDisplay(resource: Resource): string | undefined {
  const code = (resource as { code?: unknown }).code
  if (code && typeof code === 'object') {
    const cc = code as {
      text?: unknown
      coding?: { display?: unknown }[]
    }
    if (typeof cc.text === 'string' && cc.text) {
      return cc.text
    }
    const display = cc.coding?.find(
      (c) => typeof c.display === 'string' && c.display
    )?.display
    if (typeof display === 'string') {
      return display
    }
  }
  return undefined
}

// `SDRSignatureTask` → "SDR Signature Task"; keeps runs of capitals (acronyms)
// together.
function humanizeModelName(name: string): string {
  return name
    .replace(/([a-z\d])([A-Z])/g, '$1 $2')
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
}
